import { Measure } from '@fullstory/proto/fs/api/metrics';
import { Event, TimeInterval } from '@fullstory/proto/fs/api/segment';
import {
  CreateUnnamedMetricRequest,
  GetAllMetricsResponse,
  GetUnnamedMetricResponse,
  MetricDetail,
  UnnamedMetric,
} from '@fullstory/proto/fs/services/dx-objects';
import { DispatchedAction, RequestAction, createRequestReducer } from '@fullstory/redux-util';
import { createAppCacheReducer } from '@fullstory/redux-util/utils/appCache/reducers';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import {
  SearchGroup,
  dependentTypesToDependentState,
  eventTypeToEventState,
} from '@fullstory/search-model';
import { EventType } from '@fullstory/search-types';
import { mapKeys } from 'lodash';
import { Action } from 'redux';
import { getObjectWithOverlayStorageId } from '../../utils';
import { createAccessReducer } from '../../utils/accessControl';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { DashboardActions } from '../dashboards';
import { HomeRequestActions, HomeRequestTypes } from '../home';
import { SearchActions } from '../search';
import { SearchActionTypes } from '../search/types';
import { MetricsActions } from './actions';
import {
  ComputeMetricResponseWithId,
  MetricsById,
  MetricsRequestTypes,
  MetricsState,
} from './types';
import { SpaceActions, SpaceRequestTypes } from '../spaces';
import { InsightRequestTypes, MetricsInsightsRequestActions } from '../metricsInsights';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

const getMetricId = (metric: MetricDetail | null): string | undefined => metric?.metric?.metric?.id;

export const metrics = (
  state: MetricsState = {},
  action:
    | MetricsActions
    | DashboardActions
    | HomeRequestActions
    | AppBackendActions
    | SpaceActions
    | MetricsInsightsRequestActions
    | BulkRequestActions,
): MetricsState => {
  switch (action.type) {
    case MetricsRequestTypes.GetAllMetricsSuccess: {
      return action.data.metrics.reduce<MetricsById>(
        (acc, val) => {
          const id = getMetricId(val);

          if (id != null) acc[id] = val;

          return acc;
        },
        { ...state },
      );
    }
    case HomeRequestTypes.DuplicateHomeCardSuccess:
    case MetricsRequestTypes.GetMetricSuccess: {
      const innerMetric = action.data.metric;
      const id = innerMetric ? getMetricId(innerMetric) : null;
      return innerMetric == null || id == null ? state : { ...state, [id]: innerMetric };
    }
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
    case AppBackendRequestTypes.GetMetricDetailViewSuccess:
      if (!action.data.deps?.metrics) return state;
      // If we have an overlay applied, store the metric with overlay
      // separately so that we can don't overwrite the metric definition everywhere
      // and so that we can compare the saved metric to the metric with an overlay
      if (action.data.overlay && action.data.deps.metrics) {
        const metrics = action.data.deps.metrics;
        const overlayId = action.data.overlay.id;
        const updatedMetrics = mapKeys(metrics, (_, key) =>
          getObjectWithOverlayStorageId(key, overlayId),
        );
        return { ...state, ...updatedMetrics };
      }
      return { ...state, ...action.data.deps.metrics };
    case InsightRequestTypes.GetInsightDetailViewSuccess:
    case AppBackendRequestTypes.GetHomeViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data.deps?.metrics) return state;
      return { ...state, ...action.data.deps.metrics };
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const updated = { ...state };
      const allObjects = action.data.sections.flatMap(s => s.cards.flatMap(c => c.object));

      for (const object of allObjects) {
        if (object?.metric && object.metric.metric?.metric?.id) {
          updated[object.metric.metric.metric.id] = object.metric;
        }
      }

      return updated;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const metrics = action.data.objects.reduce<MetricsState>((acc, o) => {
        if (o.metric?.metric?.metric?.id) acc[o.metric?.metric?.metric?.id] = o.metric;
        return acc;
      }, {});

      return Object.keys(metrics).length
        ? {
            ...state,
            ...metrics,
          }
        : state;
    }
    case MetricsRequestTypes.SetMetricSuccess: {
      const { metric } = action.data;
      const id = getMetricId(metric);

      return metric == null || id == null ? state : { ...state, [id]: metric };
    }
    case MetricsRequestTypes.MarkViewedSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { lastViewed } = action.data;
      const { metricId } = body;
      const metric = state?.[metricId];

      return metric == null ? state : { ...state, [metricId]: { ...metric, lastViewed } };
    }
    case MetricsRequestTypes.StarMetricSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { metricId, starred } = body;
      const metric = state?.[metricId];

      return metric == null ? state : { ...state, [metricId]: { ...metric, starred } };
    }
    case MetricsRequestTypes.ArchiveMetricSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const { metricId, archived } = body;
      const metric = state?.[metricId];
      const metricRecord = metric?.metric;
      const metadata = metricRecord?.metadata;

      return metric == null || metricRecord == null || metadata == null
        ? state
        : {
            ...state,
            [metricId]: {
              ...metric,
              metric: {
                ...metricRecord,
                metadata: { ...metadata, archived },
              },
            },
          };
    }
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      const { body } = action.request;

      if (!body) return state;

      let newState = { ...state };
      const metricsReq = body.metrics;

      if (metricsReq != null) {
        metricsReq.metrics.forEach(m => {
          const { metricId, archived } = m;
          const metric = newState?.[metricId];
          const metricRecord = metric?.metric;
          const metadata = metricRecord?.metadata;

          if (metric && metricRecord && metadata) {
            newState = {
              ...newState,
              [metricId]: {
                ...metric,
                metric: {
                  ...metricRecord,
                  metadata: { ...metadata, archived },
                },
              },
            };
          }
        });
      }

      return newState;
    }
  }
  return state;
};

const computedMetric = createAppCacheReducer<ComputeMetricResponseWithId[]>()(
  MetricsRequestTypes.MetricsAppCacheIsLoading,
  MetricsRequestTypes.MetricsAppCacheLoadFailed,
  MetricsRequestTypes.MetricsAppCacheRefetching,
  MetricsRequestTypes.MetricsAppCacheHasData,
  MetricsRequestTypes.MetricsAppCacheFresh,
  MetricsRequestTypes.MetricsAppCacheSwap,
  MetricsRequestTypes.MetricsAppCacheClear,
)();
const allMetrics = createRequestReducer<GetAllMetricsResponse>()(
  MetricsRequestTypes.GetAllMetricsRequest,
  MetricsRequestTypes.GetAllMetricsSuccess,
  MetricsRequestTypes.GetAllMetricsFailure,
);

type UnnamedMetricsState = { [hash: string]: UnnamedMetric };
const unnamedMetricsRequests = createRequestReducer<UnnamedMetricsState, GetUnnamedMetricResponse>({
  mergeData: (state, update) => {
    const { hash, metric } = update;
    if (!metric) return state;

    return {
      ...state,
      [hash]: metric,
    };
  },
})(
  MetricsRequestTypes.GetUnnamedMetricRequest,
  MetricsRequestTypes.GetUnnamedMetricSuccess,
  MetricsRequestTypes.GetUnnamedMetricFailure,
);

type CreateUnnamedMetricRequestAction = RequestAction<
  MetricsRequestTypes.CreateUnnamedMetricRequest,
  CreateUnnamedMetricRequest
>;
export const isCreateUnnamedMetricRequestAction = (
  action: Action,
): action is CreateUnnamedMetricRequestAction =>
  (action as CreateUnnamedMetricRequestAction).type ===
  MetricsRequestTypes.CreateUnnamedMetricRequest;

type UnnamedMetricsRequestState = ReturnType<typeof unnamedMetricsRequests>;
const unnamedMetrics = (
  state: UnnamedMetricsRequestState = {
    lastRequested: -1,
    lastResponded: -1,
    loading: false,
    error: false,
    data: null,
    safeMessage: null,
  },
  action: CreateUnnamedMetricRequestAction | DispatchedAction<any, any, any, any, any>,
): UnnamedMetricsRequestState => {
  if (isCreateUnnamedMetricRequestAction(action)) {
    if (!action.body || !action.body.metric) return state;
    return {
      ...state,
      data: {
        ...state.data,
        [action.body.hash]: action.body.metric,
      },
    };
  }
  return unnamedMetricsRequests(state, action);
};

const access = createAccessReducer(
  MetricsRequestTypes.MetricsAccessRequest,
  MetricsRequestTypes.MetricsAccessSuccess,
  MetricsRequestTypes.MetricsAccessFailure,
);

const omnisearch = (
  state: MetricDetail | null = null,
  action: SearchActions,
): MetricDetail | null => {
  switch (action.type) {
    case SearchActionTypes.AddRow:
      if (action.mode !== 'metric') return state;
      return metricWithEvent(eventTypeToEventState(action.data as SearchGroup<EventType>));
    case SearchActionTypes.AppendDependents:
      if (action.mode !== 'metric') return state;
      return metricWithEvent({
        primary: null,
        deps: dependentTypesToDependentState(action.data),
      });
  }
  return state;
};

const metricWithEvent = (event: Event): MetricDetail => ({
  metric: {
    metric: {
      id: '',
      name: '',
      description: '',
      metric: {
        single: {
          event,
          aggregation: { default: {} },
          rollingWindow: 0,
          rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
          rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
        },
      },
      funnelFilter: null,
    },
    metadata: null,
    isDcmCreated: false,
    isCanned: false,
  },
  starred: false,
  lastViewed: null,
  canned: false,
});

type MetricsReducerState = {
  allMetrics: ReturnType<typeof allMetrics>;
  unnamedMetrics: ReturnType<typeof unnamedMetrics>;
  metrics: ReturnType<typeof metrics>;
  computedMetric: ReturnType<typeof computedMetric>;
  access: ReturnType<typeof access>;
  omnisearch: ReturnType<typeof omnisearch>;
};

export const metricsReducer = combineReducers<MetricsReducerState>({
  allMetrics,
  unnamedMetrics,
  metrics,
  computedMetric,
  access,
  omnisearch,
});
